import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from './context/AppContext';
import { Link } from 'react-router-dom';
import { MdOutlinePhone } from 'react-icons/md';
import { FiMail } from 'react-icons/fi';
import useDynamicPath from '../hooks/useDynamicPath';
import constants from '../constants.json' assert { type: 'json' };
import WeAccept from './WeAccept';
import '../styles/Footer.css';
import { ToastContext } from './context/ToastContext';

const FOOTER_COLUMN = 'footer-column';

const Footer = () => {
    const [collectionLinks, setCollectionLinks] = useState([]);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const [brandLinks, setBrandLinks] = useState([]);
    const { appContext } = useContext(AppContext);
    const { sendMessage } = useContext(ToastContext);
    const date = new Date();
    const appConfig = appContext.config.themeConfig;
    const phoneNumber = useMemo(
        () => appContext.config.contactPhoneNumber,
        [appContext.config.contactPhoneNumber],
    );
    const storeEmail = useMemo(
        () => appContext.config.storeEmail,
        [appContext.config.storeEmail],
    );

    const fetchCollections = useCallback(async () => {
        try {
            const url = new URL('api/nav', process.env.APP_URL);
            url.searchParams.append('collectionUrlPrefix', collectionPath[0].pathBeforeParams);
            url.searchParams.append('imagePlacement', appConfig.imagePlacements?.collection?.menuImage);
            url.searchParams.append('depth', 2);
            const response = await fetch(url);
            const data = await response.json();
            setCollectionLinks(data.navData || []);
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, []);

    const fetchBrands = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.append('page', 0);
            params.append('limit', 10);
            params.append('sortBy', 'PRODUCT_COUNT');
            params.append('order', 'DESC');
            const response = await fetch(new URL(`api/products/vendors/fullDetail?${params}`, process.env.APP_URL));
            const data = await response.json();
            setBrandLinks(data.vendors ?? []);
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, []);

    useEffect(() => {
        fetchCollections();
        fetchBrands();
    }, []);

    return (
        <footer
            className={'footer'}>
            <div
                className={'footer-container'}>
                <div
                    className={'footer-columns'}>
                    <div
                        className={FOOTER_COLUMN}>
                        <h6>NAVIGATE</h6>
                        <ul>
                            {appConfig.footerNavLinks?.navigate.map((link) => {
                                return (
                                    <li
                                        key={`nav-${link.url}-${link.text}`}><Link
                                            to={link.url}>{link.text}</Link></li>
                                );
                            })}
                        </ul>
                    </div>
                    <div
                        className={FOOTER_COLUMN}>
                        <h6>CATEGORIES</h6>
                        <ul>
                            {collectionLinks.map((link) => (
                                <li
                                    key={`collection-${link.url}-${link.title}`}><Link
                                        to={link.url}>{link.title}</Link></li>
                            ))}
                        </ul>
                    </div>
                    <div
                        className={FOOTER_COLUMN}>
                        <h6>POPULAR BRANDS</h6>
                        <ul>
                            {brandLinks.map((brand) => (
                                <li
                                    key={`brand-${brand.name}`}><Link
                                        to={`/brands/${brand.name}`}>{brand.name}</Link></li>
                            ))}
                        </ul>
                    </div>
                    <div
                        className={FOOTER_COLUMN}>
                        <h6>MY ACCOUNT</h6>
                        <ul>
                            {appConfig.footerNavLinks?.myAccount.map((link) => (
                                <li
                                    key={`account-${link.url}-${link.text}`}><Link
                                        to={link.url}>{link.text}</Link></li>
                            ))}
                        </ul>
                    </div>
                    <div
                        className={FOOTER_COLUMN}>
                        <h6>CONNECT WITH US</h6>
                        <br />
                        <p>
                            <strong>CALL US</strong>
                            <br />
                            <br />
                            <span
                                className={'me-4'}>
                                <MdOutlinePhone />  <a
                                    href={`tel:${phoneNumber}`}
                                    className={'text-white'}>{phoneNumber}</a>
                            </span>
                            <br />
                            Mon-Fri 6AM-8PM PST
                            <br />
                            Sat-Sun 6AM-3PM PST
                        </p>
                        <p><strong>DO YOU HAVE ANY QUESTIONS?</strong>
                            <br />
                            <br />
                            <span>
                                <FiMail /> <a
                                    className={'text-white'}
                                    href={`mailto:${storeEmail}`}>
                                    {storeEmail}
                                </a>
                            </span></p>
                        <p>1733 H St Ste 450 Blaine, WA 98230<br />United States of America</p>
                        <p
                            className={'m-0 p-0'}><b>Shop with confidence</b></p>
                        <Link
                            to={appConfig.siteShopperApprovedUrl}>
                            <img
                                className={'rating-badge'}
                                src={'https://www.shopperapproved.com/seal/38691/default-sa-seal.gif'}
                                alt={'4.7 stars'} />
                        </Link>
                    </div>
                </div>
                <div
                    className={'footer-bottom'}>
                    <p>&copy; copyright {date.getFullYear()} Pool N Spa Store.</p>
                    <div
                        className={'payment-icons'}>
                        <WeAccept />
                        <p
                            className={'m-0'}>This site is protected by reCAPTCHA and the Google <a
                                href={'https://policies.google.com/privacy'}>Privacy Policy</a> and <a
                                href={'https://policies.google.com/terms'}>Terms of Service</a> apply.
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
